import { useQuery } from '@tanstack/react-query'
import { useIsAnonymousRoute } from '@/lib/anonymous-route'
import { trpc } from '@/trpc/client'
import { useStopImpersonation } from './use-stop-impersonation'

export function ImpersonationBanner() {
  // Mounted by the ROOT route, so it renders on `/`, `/login` and `/read` as
  // readily as on `/app` — and `user.me` is a `protectedProcedure`, so on those
  // it was an authenticated query that could only 401, issued on every
  // anonymous landing hit.
  //
  // It also cost more than one wasted request. `httpBatchLink` merges by time
  // window, so on `/read?token=…` this query travelled as
  // `GET /api/trpc/user.me,studio.getShared` — and the catch-all limiter's
  // exemption for `studio.getShared` requires EVERY name in the batch to be
  // exempt, so a share read has never once been exempt on a real page load.
  // Measured in the browser, not inferred. Skipping the query here is what
  // makes that exemption fire, and the share read is left alone on its own
  // `studio-share-read` ceiling rather than also spending the 120/min bucket
  // every other tRPC caller on that address depends on.
  const isAnonymousRoute = useIsAnonymousRoute()
  const { data: me } = useQuery({
    ...trpc.user.me.queryOptions(),
    enabled: !isAnonymousRoute,
  })
  // The mutation's 200 proves the server ISSUED the clear, never that this
  // browser applied it — so the hook re-reads the effective identity and only
  // navigates once the pair is observably gone. See `use-stop-impersonation.ts`.
  const { stop, isPending } = useStopImpersonation()

  if (!me?.impersonatingFrom) return null

  const targetName = me.name ?? 'another user'
  const targetEmail = me.email

  return (
    <div
      // Kept clear of the surface nav, which is responsive: the top bar shows
      // only on desktop (sm+) and the floating tab bar only on mobile. So the
      // pill anchors to the top on mobile and the bottom on desktop — never
      // over a nav. env() inside calc() clears the notch / home indicator
      // (Tailwind v4 handles env() in calc; it only drops `max()` with a comma).
      className="fixed left-1/2 z-50 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+0.75rem)] sm:top-auto sm:bottom-[calc(env(safe-area-inset-bottom)+0.75rem)]"
    >
      <div className="flex items-center gap-3 bg-amber-500 text-amber-950 pl-4 pr-2 py-1.5 rounded-full shadow-lg text-xs font-medium">
        <span>
          Viewing as <strong>{targetName}</strong>
          {targetEmail ? ` (${targetEmail})` : ''}
        </span>
        <button
          type="button"
          onClick={stop}
          disabled={isPending}
          className="px-2.5 py-1 bg-amber-700 text-white rounded-full text-xs font-bold hover:bg-amber-800 shrink-0 disabled:opacity-60"
        >
          {isPending ? 'Stopping…' : 'Stop'}
        </button>
      </div>
    </div>
  )
}
